import { AUTH_CONFIG } from '../config/authConfig';

const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];

export function getLastActivityTimestamp(isAdmin: boolean): number {
  return Number(localStorage.getItem(isAdmin ? 'adminLastActivityTimestamp' : 'lastActivityTimestamp') || '0');
}

export function setLastActivityTimestamp(isAdmin: boolean, time: number): void {
  localStorage.setItem(isAdmin ? 'adminLastActivityTimestamp' : 'lastActivityTimestamp', time.toString());
}

export function checkInactivity(isAdmin: boolean): boolean {
  const token = localStorage.getItem(isAdmin ? 'admin_token' : 'access_token');
  if (!token) return false;

  const lastActivity = getLastActivityTimestamp(isAdmin);
  if (lastActivity === 0) return false;

  const timeoutMs = AUTH_CONFIG.INACTIVITY_TIMEOUT_HOURS * 60 * 60 * 1000;
  return Date.now() - lastActivity > timeoutMs;
}

let lastWrite = 0;
export function updateActivity(isAdmin: boolean): void {
  const token = localStorage.getItem(isAdmin ? 'admin_token' : 'access_token');
  if (!token) return;

  const now = Date.now();
  // Throttle writes to localStorage to at most once every 10 seconds
  if (now - lastWrite > 10000) {
    setLastActivityTimestamp(isAdmin, now);
    lastWrite = now;
  }
}

export function clearSession(isAdmin: boolean): void {
  if (isAdmin) {
    const keys = [
      'admin_token',
      'admin_username',
      'admin_id',
      'admin_profile',
      'adminLastActivityTimestamp'
    ];
    keys.forEach(k => localStorage.removeItem(k));
    window.location.replace('/admin/login');
  } else {
    const keys = [
      'access_token',
      'user_id',
      'username',
      'full_name',
      'account_type',
      'agency_id',
      'logo_url',
      'oauth_access_token',
      'lastActivityTimestamp'
    ];
    keys.forEach(k => localStorage.removeItem(k));
    window.location.replace('/');
  }
}

export function initializeActivityTracking(isAdmin: boolean, onTimeout: () => void): () => void {
  const timeoutMs = AUTH_CONFIG.INACTIVITY_TIMEOUT_HOURS * 60 * 60 * 1000;

  const touch = () => {
    const now = Date.now();
    const token = localStorage.getItem(isAdmin ? 'admin_token' : 'access_token');
    if (!token) return;

    // Check if session has already expired before touching
    const lastActivity = getLastActivityTimestamp(isAdmin);
    if (lastActivity > 0 && now - lastActivity > timeoutMs) {
      onTimeout();
      return;
    }

    updateActivity(isAdmin);
  };

  // Listen for user activity
  ACTIVITY_EVENTS.forEach(event => {
    window.addEventListener(event, touch, { passive: true });
  });

  // Check periodically for inactivity timeout
  const interval = setInterval(() => {
    if (checkInactivity(isAdmin)) {
      onTimeout();
    }
  }, 10000);

  // Sync logout across tabs using storage event listener
  const handleStorageChange = (e: StorageEvent) => {
    const targetKey = isAdmin ? 'admin_token' : 'access_token';
    if (e.key === targetKey && !e.newValue) {
      // Token was removed (logout occurred in another tab)
      onTimeout();
    }
  };
  window.addEventListener('storage', handleStorageChange);

  // Return cleanup function
  return () => {
    ACTIVITY_EVENTS.forEach(event => {
      window.removeEventListener(event, touch);
    });
    clearInterval(interval);
    window.removeEventListener('storage', handleStorageChange);
  };
}
